import React, {
  useMemo,
  useState,
  useContext,
  useRef,
  useLayoutEffect,
  useEffect,
  useCallback
} from 'react';
import classNames from 'classnames';
import Transparent from '@assets/images/chroma-key.png';
import type { MediaViewerMobileActionVisibility } from '@pages/MediaViewer/components/MediaViewerMobileAction/MediaViewerMobileAction';
import { AssetVersionItemDto, ImageMetadataDto } from '@api/Api';
import PanelAction from '@pages/MediaViewer/AssetViewer/PanelAction';
import PlaybackPreparingLoader, {
  DELAYED_LOADER_MS
} from '@pages/MediaViewer/AssetViewer/PlaybackPreparingLoader';
import { useAssetViewerState } from '@hooks/useAssetViewerState';
import { FabricContext } from '@context/FabricContext';
import FabricCanvas from '@pages/MediaViewer/AssetViewer/Canvas/Canvas';
import type { AssetViewerZoomOption } from '@context/AssetViewerStateContext/types';
import { useResponsive } from '@hooks/useResponsive';
import {
  ImagePinchGestureState,
  useMobileImagePinchZoom
} from '@pages/MediaViewer/AssetViewer/ImageCard/hooks/useMobileImagePinchZoom';
import { isDrawToolInUse } from '@pages/MediaViewer/helpers/drawToolShape';

import './ImageCard.less';

interface IImageCardProps {
  isExpand: boolean;
  setIsExpand(val: boolean): void;
  onReady: () => void;
  asset: AssetVersionItemDto;
  isPreparing: boolean;
  isAssetReady: boolean;
  mobileActionVisibility?: MediaViewerMobileActionVisibility | null;
}

let IS_MOUSE_DOWN = false;
let OFFSET = [0, 0];
// The viewer is remounted for every asset. The image shown last is kept on
// screen while the next one loads (fast loads then swap without an empty frame)
type ShownImage = {
  src: string;
  style: React.CSSProperties;
  hiddenAt: number;
};
let lastShownImage: ShownImage | null = null;
// Only when navigating, not when the viewer is opened again later
const LAST_SHOWN_IMAGE_MAX_AGE_MS = 1000;

function takeLastShownImage() {
  const image = lastShownImage;
  lastShownImage = null;
  if (!image || Date.now() - image.hiddenAt > LAST_SHOWN_IMAGE_MAX_AGE_MS) {
    return null;
  }
  return image;
}

function ImageCard({
  isExpand,
  setIsExpand,
  onReady,
  asset,
  isPreparing,
  isAssetReady,
  mobileActionVisibility
}: IImageCardProps) {
  const fabricContext = useContext(FabricContext);
  const containerRef = useRef<HTMLDivElement>(null);
  const viewportRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);
  const canvasContainerRef = useRef<HTMLDivElement>(null);
  const imagePinchGestureRef = useRef<ImagePinchGestureState>({
    isActive: false,
    startSpan: 0,
    baseZoom: 1,
    focalX: 0,
    focalY: 0,
    startCenterX: 0,
    startCenterY: 0
  });

  const {
    shapesDrawingTools,
    isSelectedCanvasObject,
    zoomFactor,
    zoomOption,
    turnoverDegree,
    setTurnoverDegree,
    setZoomFactor,
    setZoomOption,
    isMobileDrawingToolbarOpen,
    selectedCommentId
  } = useAssetViewerState();
  const { shape } = shapesDrawingTools;
  const { isMdWidth, isLgWidth } = useResponsive();
  const isShapeDrawing = isDrawToolInUse({
    shape,
    isMdWidth,
    isMobileDrawingToolbarOpen,
    selectedCommentId
  });

  const fileUrl = useMemo(() => {
    const { proxy } = asset.metadata as ImageMetadataDto;
    return proxy.url || '';
  }, [asset]);
  // Previous asset's image, until this one loads or the loader covers it
  const [previousImage] = useState(takeLastShownImage);
  const [isSlowLoad, setIsSlowLoad] = useState(false);
  useEffect(() => {
    if (isAssetReady) return;
    const timeout = setTimeout(() => setIsSlowLoad(true), DELAYED_LOADER_MS);
    return () => clearTimeout(timeout);
  }, [isAssetReady]);
  const isPreviousImageShown =
    !!previousImage && !isAssetReady && !isPreparing && !isSlowLoad;

  const [imageSize, setImageSize] = useState({
    width: (asset.metadata as ImageMetadataDto).info.imageInfo?.width ?? 1280,
    height: (asset.metadata as ImageMetadataDto).info.imageInfo?.height ?? 720
  });

  const getZoomFactorFromOption = useCallback(
    (option: NonNullable<AssetViewerZoomOption>) => {
      if (option === 'actual-size') return 1;
      const container = containerRef?.current;
      const image = imageRef?.current;
      if (!container || !image) return 1;
      const width =
        Math.abs(imageSize.width * Math.cos(turnoverDegree * (Math.PI / 180))) +
        Math.abs(imageSize.height * Math.sin(turnoverDegree * (Math.PI / 180)));
      const height =
        Math.abs(imageSize.width * Math.sin(turnoverDegree * (Math.PI / 180))) +
        Math.abs(imageSize.height * Math.cos(turnoverDegree * (Math.PI / 180)));
      const containerWidth = container.offsetWidth;
      const containerHeight = container.offsetHeight;
      const scaleWidth = Math.floor(containerWidth / (width / 100));
      const scaleHeight = Math.floor(containerHeight / (height / 100));
      if (option === 'auto' || option === 'fit-page') {
        return Math.min(scaleWidth, scaleHeight) / 100;
      }
      if (option === 'fit-width') {
        return scaleWidth / 100;
      }
      if (option === 'fit-height') {
        return scaleHeight / 100;
      }
      return 1;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [imageSize, isExpand, turnoverDegree]
  );

  const setZoom = useCallback(
    (v: number | NonNullable<AssetViewerZoomOption>) => {
      if (typeof v === 'number') {
        setZoomFactor(v);
      } else {
        const newZoomFactor = getZoomFactorFromOption(v);
        setZoomFactor(newZoomFactor, v);
        if (!imageRef.current || !canvasContainerRef.current) return;
        imageRef.current.style.left = '50%';
        imageRef.current.style.top = '50%';
        canvasContainerRef.current.style.left = '50%';
        canvasContainerRef.current.style.top = '50%';
      }
    },
    [getZoomFactorFromOption, setZoomFactor]
  );

  // Layout effects: zoom and overlay size are applied before the image is
  // painted, otherwise it shows at a stale zoom and then jumps to the fit
  useLayoutEffect(() => {
    setZoomOption('auto');
  }, [isLgWidth, setZoomOption]);

  useLayoutEffect(() => {
    if (!zoomOption) return;
    if (imagePinchGestureRef.current.isActive) return;
    const newZoomFactor = getZoomFactorFromOption(zoomOption);
    setZoomFactor(newZoomFactor, zoomOption);
  }, [zoomOption, getZoomFactorFromOption, setZoomFactor]);

  useMobileImagePinchZoom({
    enabled: !isLgWidth && !isShapeDrawing && !isSelectedCanvasObject,
    viewportRef,
    imageRef,
    canvasContainerRef,
    imagePinchGestureRef,
    turnoverDegree,
    zoomFactor,
    setZoomFactor
  });

  useLayoutEffect(() => {
    const image = imageRef?.current;
    const container = canvasContainerRef?.current;
    if (!image || !container || !fabricContext.canvas) return;
    const rect = image.getBoundingClientRect();

    container.style.width = `${rect.width}px`;
    container.style.height = `${rect.height}px`;
    fabricContext.canvas.setWidth(rect.width);
    fabricContext.canvas.setHeight(rect.height);
    fabricContext.canvas.setViewportTransform([
      zoomFactor * 10,
      0,
      0,
      zoomFactor * 10,
      0,
      0
    ]);
    canvasContainerRef.current.style.translate = `-${Math.round(
      rect.width / 2
    )}px -${Math.round(rect.height / 2)}px`;
  }, [imageSize, zoomFactor, fabricContext.canvas, turnoverDegree]);

  const onMouseDown = (e: any) => {
    if (!imageRef.current) return;
    if (imagePinchGestureRef.current.isActive) return;
    if (e.touches?.length > 1) {
      IS_MOUSE_DOWN = false;
      return;
    }
    IS_MOUSE_DOWN = true;
    OFFSET = [
      imageRef.current.offsetLeft - (e.clientX || e.touches?.[0]?.clientX || 0),
      imageRef.current.offsetTop - (e.clientY || e.touches?.[0]?.clientY || 0)
    ];
  };
  const onMouseUp = () => {
    IS_MOUSE_DOWN = false;
  };
  const onMouseMove = (e: any) => {
    if (isShapeDrawing || isSelectedCanvasObject) return;
    if (imagePinchGestureRef.current.isActive) return;
    if (e.touches?.length > 1) return;
    if (!imageRef.current || !canvasContainerRef.current) return;
    if (!IS_MOUSE_DOWN) return;
    e.preventDefault();

    const mousePosition = {
      x: e.clientX || e.touches?.[0]?.clientX,
      y: e.clientY || e.touches?.[0]?.clientY
    };
    imageRef.current.style.left = `${mousePosition.x + OFFSET[0]}px`;
    imageRef.current.style.top = `${mousePosition.y + OFFSET[1]}px`;
    canvasContainerRef.current.style.left = `${mousePosition.x + OFFSET[0]}px`;
    canvasContainerRef.current.style.top = `${mousePosition.y + OFFSET[1]}px`;
  };
  const onMouseLeave = () => {
    if (imagePinchGestureRef.current.isActive) return;
    IS_MOUSE_DOWN = false;
  };

  const showPanelAction =
    !mobileActionVisibility || mobileActionVisibility.showPanelAction;

  const panelActionConfig = useMemo(
    () => ({
      zoom: zoomFactor,
      zoomOption,
      setZoom,
      isShapeDrawingToolsOpen: isMobileDrawingToolbarOpen,
      isExpand,
      setIsExpand: (v: boolean) => {
        if (!imageRef.current || !canvasContainerRef.current) return;
        imageRef.current.style.left = '50%';
        imageRef.current.style.top = '50%';
        canvasContainerRef.current.style.left = '50%';
        canvasContainerRef.current.style.top = '50%';
        setIsExpand(v);
      },
      setRotate: (v: number) => {
        const rt = turnoverDegree + v === 360 ? 0 : turnoverDegree + v;
        setTurnoverDegree(rt);
      }
    }),
    [
      zoomFactor,
      zoomOption,
      setZoom,
      isMobileDrawingToolbarOpen,
      isExpand,
      turnoverDegree,
      setTurnoverDegree,
      setIsExpand
    ]
  );

  const onImageLoad = (img: HTMLImageElement) => {
    setImageSize({
      width: img.naturalWidth,
      height: img.naturalHeight
    });
    onReady();
  };

  // Cached images are complete right away: show them before the first paint
  useLayoutEffect(() => {
    const img = imageRef.current;
    if (img?.complete && img.naturalWidth) onImageLoad(img);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Remember what is on screen when navigating away, for the next viewer
  const isAssetReadyRef = useRef(isAssetReady);
  isAssetReadyRef.current = isAssetReady;
  const isPreviousImageShownRef = useRef(isPreviousImageShown);
  isPreviousImageShownRef.current = isPreviousImageShown;
  useLayoutEffect(() => {
    const img = imageRef.current;
    return () => {
      if (img && isAssetReadyRef.current) {
        const { width, height, left, top, transform } = img.style;
        lastShownImage = {
          src: img.currentSrc || img.src,
          style: { width, height, left, top, transform },
          hiddenAt: Date.now()
        };
      } else if (previousImage && isPreviousImageShownRef.current) {
        lastShownImage = { ...previousImage, hiddenAt: Date.now() };
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const imageStyle = {
    ...imageSize,
    transform: `translateX(-50%) translateY(-50%)  rotate(${turnoverDegree}deg) scale(${zoomFactor})`
  };

  return (
    <div className="media_viewer_image" ref={containerRef}>
      <div className="media_viewer_image__container" ref={viewportRef}>
        {isPreviousImageShown && (
          <img
            src={previousImage.src}
            alt=""
            draggable={false}
            style={{ ...previousImage.style, pointerEvents: 'none' }}
          />
        )}
        <img
          ref={imageRef}
          onLoad={(e) => {
            const img = e.target as HTMLImageElement;
            // Decode first, so swapping from the previous image does not
            // paint an empty frame
            img
              .decode()
              .catch(() => undefined)
              .then(() => onImageLoad(img));
          }}
          onMouseDown={onMouseDown}
          onTouchStart={onMouseDown}
          onMouseUp={onMouseUp}
          onMouseMove={onMouseMove}
          onTouchMove={onMouseMove}
          onMouseLeave={onMouseLeave}
          onTouchEnd={onMouseLeave}
          src={fileUrl}
          alt="#"
          style={{
            ...imageStyle,
            backgroundImage: `url("${Transparent}")`,
            // Shown once fully loaded, not painted progressively
            opacity: isAssetReady ? 1 : 0
          }}
        />
        {isPreparing ? (
          <PlaybackPreparingLoader />
        ) : (
          !isAssetReady && <PlaybackPreparingLoader delayed />
        )}

        <div
          onMouseDown={onMouseDown}
          onTouchStart={onMouseDown}
          onMouseUp={onMouseUp}
          onMouseMove={onMouseMove}
          onTouchMove={onMouseMove}
          onMouseLeave={onMouseLeave}
          onTouchEnd={onMouseLeave}
          ref={canvasContainerRef}
          className={classNames('media_viewer_image__canvas', {
            'media_viewer_image__canvas--draggable': !isShapeDrawing
          })}
          style={{
            width: '100%',
            height: '100%'
          }}
        >
          <FabricCanvas />
        </div>
        {showPanelAction && <PanelAction {...panelActionConfig} />}
      </div>
    </div>
  );
}

export default ImageCard;
